import { SearchIcon } from "@heroicons/react/outline";
import compact from "lodash/compact";
import { MouseEvent, MouseEventHandler, SVGProps, useState } from "react";
import { IconType } from "react-icons";
import { TbPlus } from "react-icons/tb";
import { useLocation } from "react-router-dom";
import { CreatedArtifactFragmentFragment } from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import ArtifactCreationDialog from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import useLabel from "@apps/use-label/use-label";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Link from "@components/link/link";
import { useLink } from "@components/link/link-helpers";
import Tooltip from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { getUrl, toWithBackground } from "@helpers/helpers";

export const sidebarButtonClassName = "px-2 py-1 rounded";

type Props = {
  onClickSearch: () => void;
  menuIsCollapsed: boolean;
};

type SidebarNavItem = {
  id: string;
  name: string;
  href: string;
  icon?: IconType | ((props: SVGProps<SVGSVGElement>) => JSX.Element);
  activeUrls: string[];
};

const getButtonClassName = (isActive = false, menuIsCollapsed: boolean) => {
  return classNames(
    "group flex items-center text-sm font-normal gap-2 text-left",
    sidebarButtonClassName,
    menuIsCollapsed ? "justify-center" : "justify-start flex-1 w-full",
    isActive
      ? "text-gray-800 bg-[#F1F3F7]"
      : "text-gray-800 hover:bg-[#F1F3F7]",
  );
};

const SidebarNavigationItem = ({
  item,
  menuIsCollapsed = false,
  onClickNavigation,
  isActive,
}: {
  item: SidebarNavItem;
  menuIsCollapsed: boolean;
  onClickNavigation: (
    item: SidebarNavItem,
  ) => (e: MouseEvent<HTMLAnchorElement>) => void;
  isActive: boolean;
}) => {
  const currentUser = currentUserVar();
  return (
    <div key={item.name}>
      <div className={classNames("flex fs-unmask")}>
        <Tooltip placement="right" text={menuIsCollapsed ? item.name : ""}>
          <span className="flex-1">
            <Link
              to={item.href}
              className={getButtonClassName(isActive, menuIsCollapsed)}
              onClick={onClickNavigation(item)}
            >
              {item.icon ? (
                <item.icon
                  className={classNames("shrink-0 h-4 w-4 text-gray-500")}
                />
              ) : (
                <Avatar size="4" user={currentUser} className="shrink-0" />
              )}
              {!menuIsCollapsed && item.name}
            </Link>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

const isItemActive = (item: SidebarNavItem) =>
  item.activeUrls.some((url) =>
    window.location.href.startsWith(`${window.location.origin}${url}`),
  );

const SidebarNavigation: React.FC<Props> = ({
  menuIsCollapsed,
  onClickSearch,
}: {
  menuIsCollapsed: boolean;
  onClickSearch: () => void;
}) => {
  const label = useLabel();
  const location = useLocation<TFLocationState>();
  const isAdmin = isAdminVar();
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();
  const hasReports = currentUser.directReports.edges.length > 0;
  const [showCreationDialog, setShowCreationDialog] = useState(false);
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();

  const performanceExpanded =
    uiPreferenceCache.leftNavigationPerformanceExpanded;

  const handleTogglePerformance = () => {
    saveUiPreference({
      leftNavigationPerformanceExpanded:
        !uiPreferenceCache.leftNavigationPerformanceExpanded,
    });
  };

  const mainNavigationItems: SidebarNavItem[] = compact([
    {
      id: "dashboard",
      name: "Home",
      href: "/dashboard",
      activeUrls: ["/dashboard"],
      icon: getTopicflowIcon("home"),
    },
    currentOrganization.featureFlags.profilePage && {
      id: "profile",
      name: "Workspace",
      href: `/profile/${currentUser.id}`,
      activeUrls: [`/profile/${currentUser.id}`],
    },
    currentOrganization.featureFlags.goals && {
      id: "goalOverview",
      name: label("goal", { pluralize: true, capitalize: true }),
      href: "/goals",
      activeUrls: ["/goals", "/kpis", "/goal-alignment"],
      icon: getTopicflowIcon("goal"),
    },
    currentOrganization.featureFlags.meetings && {
      id: "calendar",
      name: "Meetings",
      href: "/meetings",
      activeUrls: ["/meetings", "/meeting", "/calendar"],
      icon: getTopicflowIcon("meeting"),
    },
    currentOrganization.featureFlags.feedbacks && {
      id: "feedback",
      name: label("feedback", { capitalize: true }),
      href: "/feedbacks/received",
      activeUrls: ["/feedbacks/received"],
      icon: getTopicflowIcon("feedback"),
    },
    currentOrganization.featureFlags.recognitions && {
      id: "recognitions",
      name: label("recognition", { pluralize: true, capitalize: true }),
      href: "/recognition-board",
      activeUrls: ["/recognition-board"],
      icon: getTopicflowIcon("recognition"),
    },
  ]);

  const performanceNavigationItems: SidebarNavItem[] = compact([
    (isAdmin || hasReports) && {
      id: "reporting",
      name: "Reporting",
      href: "/reporting",
      activeUrls: ["/reporting"],
      icon: getTopicflowIcon("reporting"),
    },
    {
      id: "programs",
      name: "Programs",
      href: "/programs",
      activeUrls: ["/programs"],
      icon: getTopicflowIcon("program"),
    },
    {
      id: "engagement-survey",
      name: "Surveys",
      href: "/survey",
      activeUrls: ["/survey"],
      icon: getTopicflowIcon("survey"),
    },
    {
      id: "assessments",
      name: label("review", { capitalize: true, pluralize: true }),
      href: "/assessments",
      activeUrls: ["/assessments"],
      icon: getTopicflowIcon("assessment"),
    },
  ]);

  const handleClickNavigationItem =
    (item: SidebarNavItem): MouseEventHandler<HTMLAnchorElement> =>
    (e) => {
      if (item.id === "search") {
        e.preventDefault();
        onClickSearch();
      }
    };

  const link = useLink();
  const handleCloseCreationDialog = (
    artifact?: CreatedArtifactFragmentFragment,
  ) => {
    setShowCreationDialog(false);
    if (artifact) {
      link.redirect(
        toWithBackground({
          pathname: getUrl({
            artifactId: artifact.id,
            artifactType: artifact.artifactType,
          }),
          location,
        }),
      );
    }
  };

  const searchPlusButtonClassName =
    "text-xs bg-white shadow-sm tracking-tight px-2 py-1 rounded-sm border text-gray-400 flex items-center gap-1 hover:text-gray-600 hover:border-gray-300 hover:bg-black/5";

  return (
    <>
      {showCreationDialog && (
        <ArtifactCreationDialog onClose={handleCloseCreationDialog} />
      )}
      <div
        className={classNames(
          "flex flex-col justify-center items-center fs-unmask",
          !menuIsCollapsed ? "hidden" : "mb-3",
        )}
      >
        <Tooltip text="Search" placement="right">
          <button
            className={classNames(
              menuIsCollapsed
                ? getButtonClassName(false, menuIsCollapsed)
                : searchPlusButtonClassName,
              "mb-2",
            )}
            onClick={onClickSearch}
          >
            <SearchIcon className="h-4 w-4 text-gray-500" />
          </button>
        </Tooltip>
        <Tooltip text="Create" placement="right">
          <button
            onClick={() => setShowCreationDialog(true)}
            className={classNames(
              menuIsCollapsed
                ? getButtonClassName(false, menuIsCollapsed)
                : searchPlusButtonClassName,
              "text-gray-500",
            )}
          >
            <TbPlus className="h-4 w-4 text-gray-500" />
            {!menuIsCollapsed && <span>New</span>}
          </button>
        </Tooltip>
      </div>
      <div>
        <div
          className={classNames(
            "flex flex-col",
            menuIsCollapsed ? "justify-center items-center gap-2" : "gap-1",
          )}
        >
          {mainNavigationItems.map((item) => (
            <SidebarNavigationItem
              item={item}
              isActive={isItemActive(item)}
              menuIsCollapsed={menuIsCollapsed}
              onClickNavigation={handleClickNavigationItem}
              key={item.name}
            />
          ))}
        </div>
        {performanceNavigationItems.length > 0 && (
          <div className={classNames("pt-5", menuIsCollapsed && "pt-2")}>
            {!menuIsCollapsed && (
              <button
                className="text-gray-500 text-xs px-2 pb-1 flex items-center gap-0.5 w-full"
                onClick={handleTogglePerformance}
              >
                <span>Performance</span>
                <TopicflowIcon
                  type="chevronDown"
                  className={classNames(
                    "h-3 w-3 text-gray-500 transition-transform duration-200 ease-in-out",
                    performanceExpanded ? "rotate-180" : "",
                  )}
                />
              </button>
            )}
            <div
              className={classNames(
                "flex flex-col",
                menuIsCollapsed
                  ? "justify-center items-center gap-2"
                  : "gap-1 overflow-hidden transition-all duration-200 ease-in-out",
                !menuIsCollapsed &&
                  (performanceExpanded
                    ? "max-h-96 opacity-100"
                    : "max-h-0 opacity-0"),
              )}
            >
              {performanceNavigationItems.map((item) => (
                <SidebarNavigationItem
                  item={item}
                  isActive={isItemActive(item)}
                  menuIsCollapsed={menuIsCollapsed}
                  onClickNavigation={handleClickNavigationItem}
                  key={item.name}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SidebarNavigation;
